import { useSceneEvents } from "@/components/common/scene-events-context";
import { useThreeContext } from "@/components/common/three-context/three-context";
import { CadModelObject } from "@/object-cache";
import {
  FastSSAOPass,
  FilteredRenderPass,
  SSAOPass,
  SubScene,
  useOnResize,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  BasicRenderingPolicy,
  CadRenderingMode,
  LotvRenderer,
  OutOfCoreStreamCadModel,
  StreamCadModel,
} from "@faro-lotv/lotv";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { Object3D } from "three";
import {
  RenderDispatch,
  setCadModelRenderDispatch,
} from "./render-dispatch-utils";

type CadFilter = (obj: Object3D) => boolean;

function defaultCadFilter(obj: Object3D): boolean {
  return obj.userData.type === RenderDispatch.CadModel;
}

type CadSubsceneProps = {
  /** The CAD model to be rendered */
  cadModel?: CadModelObject | null;
  /** Whether the CAD subscene is enabled for rendering or not */
  enabled: boolean;
  /** Whether render-on-demand is enabled */
  renderOnDemand?: boolean;
  /** Optional CAD filter function */
  cadFilter?: CadFilter;
  /** Whether the CAD model needs to be labeled with a 'RenderDispatch' enum value */
  needsDispatch?: boolean;
};

/** @returns a subscene with a pipeline designed to render a CAD model */
export function CadSubscene({
  cadModel,
  enabled,
  renderOnDemand = true,
  cadFilter = defaultCadFilter,
  needsDispatch = true,
}: CadSubsceneProps): JSX.Element {
  // If the GPU supports it, use high-quality SSAO for CAD
  const { renderer } = useThreeContext();

  // basic rendering policy to implement render-on-demand
  const renderingPolicy = useMemo(
    () => new BasicRenderingPolicy(cadModel ?? undefined),
    [cadModel],
  );

  useEffect(() => {
    if (
      renderOnDemand &&
      !renderingPolicy.subScene?.cameraMonitor.cameraMoving
    ) {
      renderingPolicy.onCameraStoppedMoving();
    } else {
      renderingPolicy.onCameraStartedMoving();
    }
  }, [renderingPolicy, renderOnDemand]);

  const invalidate = useCallback(
    () => renderingPolicy.subScene?.invalidate(),
    [renderingPolicy.subScene],
  );

  const cadSceneEvents = useSceneEvents();
  useTypedEvent<void>(cadSceneEvents.invalidateCadScene, invalidate);

  useTypedEvent<void>(
    cadModel instanceof StreamCadModel ||
      cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.chunkLoaded
      : undefined,
    invalidate,
  );
  useTypedEvent<void>(
    cadModel instanceof OutOfCoreStreamCadModel
      ? cadModel.chunkDisposed
      : undefined,
    invalidate,
  );

  useOnResize(invalidate);

  // Restore cad model rendering mode to the default state
  useEffect(() => {
    const previousCadModel = cadModel;
    if (previousCadModel) {
      return () => {
        previousCadModel.renderingMode = CadRenderingMode.OpaqueScene;
      };
    }
  }, [cadModel]);

  const beforeOpaqueRender = useCallback(() => {
    if (cadModel) {
      cadModel.renderingMode = CadRenderingMode.OpaqueScene;
      if (needsDispatch) {
        setCadModelRenderDispatch(cadModel);
      }
    }
  }, [cadModel, needsDispatch]);

  const beforeTransparentRender = useCallback(() => {
    if (cadModel) {
      cadModel.renderingMode = CadRenderingMode.TransparentScene;
    }
  }, [cadModel]);

  // The subscene returned contains a pipeline structured in three steps.
  // First the opaque CAD parts are rendered, then SSAO is applied, then
  // the transparent CAD parts are rendered.
  return (
    <SubScene
      filter={cadFilter}
      enabled={enabled}
      onBeforeRender={beforeOpaqueRender}
      renderingPolicy={renderingPolicy}
    >
      {renderer instanceof LotvRenderer && renderer.supportsHighQualitySSAO ? (
        <SSAOPass strength={1.5} radius={1.5} />
      ) : (
        <FastSSAOPass strength={3.0} radius={1.5} />
      )}
      <FilteredRenderPass
        clear={false}
        clearDepth={false}
        filter={cadFilter}
        onBeforeRender={beforeTransparentRender}
      />
    </SubScene>
  );
}
